import React from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import styles from './Button.module.scss';

/* Size-specific padding is applied via inline `style` so it survives
   page-level universal resets (e.g. `.pageRoot * { padding: 0 }`)
   without any specificity workaround inside Button.module.scss. */
const SIZE_PADDING = {
  medium: '13px 24px',
  small: '8px 14px',
};

function buildRootClassName({ variant, size, disabled, hasIcon, className }) {
  return classNames(
    styles.root,
    variant === 'secondary' && styles.secondary,
    size === 'small' && styles.small,
    hasIcon && styles.withIcon,
    disabled && styles.disabled,
    className
  );
}

function buildRootStyle(size) {
  const padding = SIZE_PADDING[size] || SIZE_PADDING.medium;
  return { padding };
}

function renderContent({ children, iconLeft, iconRight, hasIcon }) {
  if (!hasIcon) return children;
  return (
    <>
      {iconLeft}
      {children != null && <span>{children}</span>}
      {iconRight}
    </>
  );
}

export default function Button({
  onClick = () => {},
  children,
  className = null,
  to = null,
  variant = 'primary',
  size = 'medium',
  disabled = false,
  type = 'button',
  iconLeft = null,
  iconRight = null,
  ariaLabel,
}) {
  const hasIcon = Boolean(iconLeft || iconRight);
  const rootClassName = buildRootClassName({ variant, size, disabled, hasIcon, className });
  const rootStyle = buildRootStyle(size);
  const content = renderContent({ children, iconLeft, iconRight, hasIcon });

  const handleLinkClick = (event) => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick(event);
  };

  if (to) {
    return (
      <Link
        to={to}
        onClick={handleLinkClick}
        className={rootClassName}
        style={rootStyle}
        aria-label={ariaLabel}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={rootClassName}
      style={rootStyle}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {content}
    </button>
  );
}
